import { MatCheckbox } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, inject, Inject, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validator } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { InputComponent } from '../input/input.component';
import { ButtonComponent } from '../button/button.component';
import { MatIconButton } from '@angular/material/button';
import { AuthService } from '../../../core/services/auth.service';

/**
 * EAP Dropdown Component
 * ----------------------
 * A reusable dropdown component that supports two behaviors:
 * 1. 'select': Standard dropdown selection
 * 2. 'navigate': Clicking an option navigates to a new route
 * 
 * Compatible with Angular Reactive Forms via ControlValueAccessor.
 */

export interface DropdownOption {
  label: string;           // Display text for the option
  value?: string | number; // Optional internal value (used in 'select' mode)
  route?: string;          // Route path (for 'navigate' mode)
  icon?: string;           // Optional Material icon name (used before label)
}

export interface MultiSelectResult {
  labels: string[];
  values: (string | number)[];
}

@Component({
  selector: 'eap-dropdown',
  standalone: true,
  imports: [CommonModule, MatIcon, InputComponent, ReactiveFormsModule,MatCheckbox, ButtonComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss'
})
export class DropdownComponent implements OnInit, ControlValueAccessor, Validator {

  //#region inputs
  @Input() label = '';                                      // Label shown above the input
  @Input() placeholder = 'Select';                          // Placeholder text inside the input
  @Input() disabled = false;                                // Disable entire dropdown interaction
  @Input() error = false;                                   // Display error state (red styles)
  @Input() behavior: 'select' | 'navigate' = 'select';      // Defines behavior on click: 'select' or 'navigate'
  @Input() options: DropdownOption[] = [];                  // List of dropdown options
  @Input() selectedLabel = '';                              // For 'navigate' mode, display-only label for selection
  @Input() isInputReadOnly: boolean = false;                 // Whether the input field should be read-only
  @Input() defaultSelected: DropdownOption | null = null;   // Default selected item (used in 'select' mode)
  @Input() isMultiSelect: boolean = false;                  // Flag to determine if it's used for multi select
  @Input() isWorkAsButton: boolean = false;                 // Flag to determine if it's work as button or input
  @Input() allowToShowPlaceHolder: boolean = false;         // Flag to determine if it's show place holder or not
  @Input() showLargeIcon: boolean = true;                  // Flag to determine if we need to show large icon
  //#endregion

  //#region output
  @Output() selectionChange = new EventEmitter<DropdownOption>(); // Emits selected option in 'select' mode
  @Output() multiSelectionChange = new EventEmitter<string>(); // Emits selected option in 'select' mode
  //#endregion

  //#region internal control & state
  control = new FormControl();                              // FormControl used by eap-input
  isOpen = signal(false);                                   // Tracks dropdown visibility
  selected = signal<DropdownOption | null>(null);           // Stores the currently selected item (for 'select' mode')
  multiSelected = signal<MultiSelectResult | null>(null);
  selectedValues = new Set<string | number>();
  //#endregion

  //#region CVA hooks
  onChange = (value: any) => {};                            // Registered by Angular forms
  onTouched = () => {};                                     // Registered by Angular forms
  //#endregion

  constructor(private router: Router, private el: ElementRef, private authService: AuthService) { }

  ngOnInit() {
    if (this.defaultSelected) {
      this.selected.set(this.defaultSelected);
      this.control.setValue(this.defaultSelected.label); // Show initial label
    }
  }

  /**
   * Toggles dropdown visibility on input/button click.
   */
  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen.update(v => !v);
    }
  }

  /**
   * Handles option selection:
   * - In 'select' mode: sets selected option and emits it.
   * - In 'navigate' mode: navigates to the option's route.
   * 
   * Also updates the form control value for reactive form binding.
   * 
   * @param option DropdownOption that was clicked
   */
  selectOption(option: DropdownOption): void {
    if(!this.isMultiSelect){
      if (this.behavior === 'navigate' && option.route) {
        this.router.navigate([option.route]);

        if (option.route.includes('login')) {
          this.authService.logout();
        }
      } else {
        this.selected.set(option);
        this.control.setValue(option.label);     // Display selected label in eap-input
        this.selectionChange.emit(option);       // Emit selected object
        this.onChange(option.value);             // Notify Reactive Forms
      }
      this.isOpen.set(false);
    } else{
      if (this.selectedValues.has(option.value!)) {
        this.selectedValues.delete(option.value!);
      } else {
        this.selectedValues.add(option.value!);
      }
    
      const selectedOptions = this.options.filter(o =>
        this.selectedValues.has(o.value!)
      );
    
      const labels = selectedOptions.map(o => o.label);
      const values = selectedOptions.map(o => o.value!);

      this.control.setValue(labels.join(', '));
      this.multiSelectionChange.emit(values.join(","));
      this.onChange(values);
    }
   
  }

  /**
   * ControlValueAccessor: Called when Reactive Form writes a value.
   * Finds the matching option based on value and displays it.
   */
  writeValue(value: any): void {
    const selectedOption = this.options.find(opt => opt.value === value) || null;
    this.selected.set(selectedOption);
    this.control.setValue(selectedOption?.label ?? '');
  }

  /**
   * ControlValueAccessor: Registers change handler from Reactive Forms.
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor: Registers touched handler from Reactive Forms.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor: Enables/disables the control via Reactive Forms.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  /**
   * Validator: Provides custom validation (optional).
   * Currently always returns null (valid).
   */
  validate(): any {
    return null;
  }

  isChecked(option: DropdownOption): boolean {
    return this.selectedValues.has(option.value || "");
  }
  

  toggleMultiSelect(option: DropdownOption, event: any) {
    const checked = event.checked;
  
    if (checked) {
      this.selectedValues.add(option.value!);
    } else {
      this.selectedValues.delete(option.value!);
    }
  
    const selectedOptions = this.options.filter(o =>
      this.selectedValues.has(o.value || "")
    );
  
    const labels = selectedOptions.map(o => o.label);
    const values = selectedOptions.map(o => o.value!);
  
    this.control.setValue(labels.join(', '));
  
    this.onChange(values);
  
    this.multiSelectionChange.emit(values.join(","));
  }
  
  @HostListener('document:click', ['$event'])
  public onBodyClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
  
}
